import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Quote, ShieldCheck, Activity, FileCheck2, Sparkles, CheckCircle2, Compass, Users, RotateCcw } from 'lucide-react';
import ImagePlaceholder from '../components/ImagePlaceholder';

const engagementSteps = [
  {
    n: '01',
    icon: Compass,
    t: 'Diagnose',
    d: 'Map your delivery landscape, surface friction, and agree the outcome with stakeholders.',
  },
  {
    n: '02',
    icon: Users,
    t: 'Embed',
    d: 'Sit alongside your team. Design the governance, then build it inside Videra so the work runs in one place.',
  },
  {
    n: '03',
    icon: RotateCcw,
    t: 'Hand back',
    d: 'Leave the team stronger and Videra running the work — repeatable, evidence-driven, audit-ready.',
  },
];

const principles = [
  { icon: ShieldCheck, t: 'Practitioner-led',  d: "We've actually run capital programmes, FM portfolios and Tier-1 builds." },
  { icon: Activity,    t: 'Embedded',          d: 'We sit alongside your team. We do the work with you, not at you.' },
  { icon: FileCheck2,  t: 'Evidence-driven',   d: 'Every decision backed by structured, auditable evidence — by default.' },
  { icon: Sparkles,    t: 'System-leaving',    d: 'When we leave, the system stays. Videra carries the work forward.' },
];

const sectorShowcase = [
  {
    img: '/images/nhs/nhs-portfolio-tracker.jpg',
    eyebrow: 'NHS Capital & Estates',
    title: 'Sixteen Trusts, one rail.',
    desc:  'Capital programmes, ERIC returns, Tier-A/B/C compliance and lifecycle planning — all visible to the CFO.',
    href: '/sectors/nhs',
    accent: 'border-blue-200 hover:border-blue-400',
  },
  {
    img: '/images/healthcare/hc-sector-dashboard.jpg',
    eyebrow: 'Healthcare · FM Hard Services',
    title: 'Every site. Every submittal.',
    desc:  'PFI hard-services on a single audit-ready framework — gate documents, RAG rollup, handback readiness.',
    href: '/sectors/hard-services',
    accent: 'border-emerald-200 hover:border-emerald-400',
  },
  {
    img: '/images/construction/cp-portfolio-pmo.jpg',
    eyebrow: 'Construction+\u2122 · Tier-1',
    title: 'Initiation to close-out.',
    desc:  'Stage-gated delivery on the CPlus 7 / RIBA framework. 60-year Golden Thread, Building Safety Act 2022-ready.',
    href: '/sectors/construction',
    accent: 'border-orange-200 hover:border-orange-400',
  },
];

const galleryStrip = [
  { f: '/images/healthcare/hc-projects-gate-overview.jpg',  l: 'Stage-gated delivery' },
  { f: '/images/nhs/nhs-compliance-rails.jpg',              l: 'Synchronised compliance' },
  { f: '/images/healthcare/hc-governance-weekly-dialog.jpg',l: 'Weekly governance' },
  { f: '/images/construction/cp-executive-report.jpg',      l: 'Board-grade reporting' },
  { f: '/images/healthcare/hc-handback-readiness.jpg',      l: 'Handback readiness' },
  { f: '/images/construction/cp-lifecycle.jpg',             l: 'Lifecycle planning' },
];

const V2About = () => (
  <div data-testid="v2-about">
    {/* ─────────── 1. HERO with imagery ─────────── */}
    <section className="relative pt-20 pb-16 md:pt-28 md:pb-20 bg-gradient-to-b from-slate-50 via-white to-white overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, #0f172a 1px, transparent 0)',
        backgroundSize: '24px 24px',
      }} />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-4">About Keystone &amp; Videra</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 leading-[1.05]">
              Structured delivery, embedded expertise,
              <br />
              <span className="text-blue-600">lasting capability.</span>
            </h1>
            <p className="mt-6 text-lg lg:text-xl text-slate-600 leading-relaxed max-w-2xl">
              Keystone is a specialist delivery consultancy built by people who have actually run NHS,
              FM and Tier&#8209;1 construction programmes. We embed alongside your teams, strengthen the
              way work flows, and build the entire operating model inside <strong className="text-slate-900">Videra</strong>,
              our digital delivery system.
            </p>
            <p className="mt-4 text-lg lg:text-xl text-slate-600 leading-relaxed max-w-2xl">
              The result is disciplined delivery, clearer governance, and a repeatable way of working
              that continues to perform long after we&apos;ve stepped away.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-3">
              <img src="/images/nhs/nhs-portfolio-tracker.jpg" alt="NHS Portfolio Tracker"
                   className="w-full aspect-[16/10] object-cover object-top rounded-lg shadow-lg" />
              <img src="/images/construction/cp-portfolio-pmo.jpg" alt="Construction+ PMO"
                   className="w-full aspect-[16/10] object-cover object-top rounded-lg shadow-lg" />
              <img src="/images/healthcare/hc-sector-dashboard.jpg" alt="Hard Services Sector Command Centre"
                   className="col-span-2 w-full aspect-[16/7] object-cover object-top rounded-lg shadow-lg" />
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ─────────── 2. HOW WE ENGAGE — 3-step strip ─────────── */}
    <section className="py-16 md:py-20 bg-slate-900 text-white relative overflow-hidden" data-testid="v2-about-engagement">
      <div className="absolute inset-0 opacity-[0.05]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '28px 28px',
      }} />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-10 md:mb-14">
          <p className="text-xs uppercase tracking-[0.2em] text-blue-400 font-semibold mb-3">How we engage</p>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-medium tracking-tight leading-tight">
            Three steps. <span className="text-slate-400">One operating model that lasts.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-800 rounded-2xl overflow-hidden border border-slate-800">
          {engagementSteps.map((s) => (
            <div
              key={s.n}
              className="bg-slate-900 p-7 md:p-8 hover:bg-slate-800/60 transition-colors"
              data-testid={`v2-about-engagement-step-${s.n}`}
            >
              <div className="flex items-center justify-between mb-5">
                <span className="w-10 h-10 rounded-lg bg-blue-500/15 border border-blue-500/30 text-blue-300 flex items-center justify-center">
                  <s.icon className="w-4 h-4" />
                </span>
                <span className="text-[11px] font-medium tracking-[0.2em] text-slate-500">STEP {s.n}</span>
              </div>
              <h3 className="text-xl md:text-2xl font-medium text-white mb-2.5">{s.t}</h3>
              <p className="text-sm md:text-base text-slate-400 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ─────────── 3. WHY VIDERA EXISTS — narrative + supporting image ─────────── */}
    <section className="py-20 md:py-28 bg-white" data-testid="v2-about-narrative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <div className="lg:col-span-6">
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-4">Why Videra exists</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 leading-[1.1]">
            Most software <span className="text-slate-400">digitises tasks.</span> Videra <span className="text-blue-600">creates control.</span>
          </h2>
          <div className="mt-6 space-y-5 text-base md:text-lg text-slate-600 leading-relaxed">
            <p>
              Organisations lack a consistent, governed, evidence-driven way to deliver projects and
              manage assets. They string together inboxes, spreadsheets and PDFs — and call it a system.
            </p>
            <p>
              Videra is the system. It pulls every project, every gate and every piece of evidence into one
              structured environment. Your teams keep doing what they do — but now everything they do is
              visible, auditable and ready when the board asks.
            </p>
          </div>
        </div>
        <div className="lg:col-span-6">
          <div className="bg-slate-900 rounded-2xl p-3 shadow-[0_30px_60px_-20px_rgba(15,23,42,0.35)]">
            <img src="/images/construction/cp-executive-report.jpg"
                 alt="Executive Report — Portfolio posture across 54 projects"
                 className="w-full aspect-[16/10] object-cover object-top rounded-lg" />
          </div>
          <p className="text-xs text-slate-500 mt-3 text-center">
            Executive Report · 54 projects · £3bn portfolio · top risks severity-weighted for the board.
          </p>
        </div>
      </div>
    </section>

    {/* ─────────── 4. CEO MESSAGE — Peter Fulford ─────────── */}
    <section className="py-20 md:py-28 bg-slate-50 border-y border-slate-200" data-testid="v2-about-ceo">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-6 text-center">A word from our founder</p>
        <div className="relative bg-white border border-slate-200 rounded-2xl p-8 md:p-12 shadow-sm">
          <Quote className="absolute -top-4 left-8 w-10 h-10 text-blue-600 bg-white p-1.5 rounded" />
          <div className="text-lg md:text-xl text-slate-700 leading-relaxed italic space-y-5">
            <p>Delivery should never be left to chance.</p>
            <p>
              Too many organisations operate in the dark — fragmented systems, inconsistent processes,
              manual reporting that hides risk rather than exposing it. We built Videra to change that.
            </p>
            <p>
              Videra is a delivery operating system that gives leaders real-time visibility, gives teams a
              clear pathway to follow, and gives organisations the confidence that every decision is backed
              by complete, auditable evidence.
            </p>
          </div>
          <div className="mt-10 flex items-center gap-5 not-italic">
            <div className="w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden flex-shrink-0 ring-4 ring-white shadow-md bg-slate-100">
              <img
                src="/images/team/peter-fulford.jpg"
                alt="Peter Fulford — Founder & CEO"
                className="w-full h-full object-cover object-center"
              />
            </div>
            <div>
              <p className="text-lg font-medium text-slate-900">Peter Fulford</p>
              <p className="text-sm text-slate-500">Founder &amp; CEO, Videra · Keystone Strategic Consultants</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ─────────── 5. WHAT WE STAND FOR — 4 principle cards ─────────── */}
    <section className="py-20 md:py-28 bg-white" data-testid="v2-about-principles">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-4">What we stand for</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 leading-[1.1]">
            Four principles, every engagement.
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {principles.map((p, i) => (
            <motion.div
              key={p.t}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '0px 0px -50px 0px' }}
              transition={{ delay: i * 0.05 }}
              className="bg-white border border-slate-200 rounded-xl p-6 hover:border-slate-400 hover:shadow-md transition-all"
            >
              <span className="w-10 h-10 rounded-md bg-blue-50 border border-blue-100 text-blue-600 inline-flex items-center justify-center">
                <p.icon className="w-5 h-5" />
              </span>
              <p className="mt-4 text-base font-medium text-slate-900">{p.t}</p>
              <p className="mt-2 text-sm text-slate-600 leading-snug">{p.d}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* ─────────── 6. SECTOR SHOWCASE — 3 cards with real screens ─────────── */}
    <section className="py-20 md:py-28 bg-slate-50 border-y border-slate-200" data-testid="v2-about-sectors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-4">Where Videra is deployed</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 leading-[1.1]">
            Three sectors. One operating system.
          </h2>
          <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed">
            Each sector workspace is shaped around the workflows that sector actually uses — but underneath, it&apos;s the same
            Videra, the same governance, the same evidence trail.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {sectorShowcase.map(s => (
            <Link
              key={s.title}
              to={s.href}
              className={`group block bg-white rounded-2xl overflow-hidden border ${s.accent} transition-all hover:shadow-lg`}
            >
              <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                <img
                  src={s.img}
                  alt={s.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <p className="text-[11px] uppercase tracking-[0.2em] text-blue-600 font-semibold">{s.eyebrow}</p>
                <h3 className="mt-2 text-xl font-medium text-slate-900">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-snug">{s.desc}</p>
                <p className="mt-4 inline-flex items-center text-sm font-medium text-slate-900 group-hover:text-blue-600 transition-colors">
                  Explore the sector <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>

    {/* ─────────── 7. HOW VIDERA LOOKS — 6-tile gallery strip ─────────── */}
    <section className="py-20 md:py-28 bg-white" data-testid="v2-about-gallery">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-4">How Videra looks today</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 leading-[1.1]">
            Real screens from the live demo.
          </h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {galleryStrip.map(g => (
            <div key={g.f} className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-lg hover:border-slate-300 transition-all">
              <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                <img src={g.f} alt={g.l} loading="lazy"
                     className="absolute inset-0 w-full h-full object-cover object-top" />
              </div>
              <div className="p-4">
                <p className="text-sm font-medium text-slate-900">{g.l}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Link
            to="/sectors"
            className="inline-flex items-center text-sm font-medium text-slate-900 hover:text-blue-600 transition-colors group"
          >
            See every sector workspace <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>

    {/* ─────────── 8. MISSION & VISION ─────────── */}
    <section className="py-20 md:py-28 bg-slate-50 border-y border-slate-200" data-testid="v2-about-mission">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-2xl p-8 md:p-10 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-blue-600" />
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-4">Mission</p>
          <p className="text-2xl md:text-3xl text-slate-900 font-medium leading-snug">
            To make delivery governed, intelligent and audit-ready — for every project, every asset, every team.
          </p>
        </div>
        <div className="bg-slate-900 text-white rounded-2xl p-8 md:p-10 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-emerald-500" />
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-semibold mb-4">Vision</p>
          <p className="text-2xl md:text-3xl font-medium leading-snug">
            A world where delivery is never left to chance — where evidence, intelligence and certainty are the default.
          </p>
        </div>
      </div>
    </section>

    {/* ─────────── 9. HOW WE WORK ─────────── */}
    <section className="py-20 md:py-28 bg-white" data-testid="v2-about-how-we-work">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        <div className="lg:col-span-6">
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-4">How we work</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 leading-[1.1]">
            Consultancy that becomes <span className="text-slate-400">infrastructure.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed">
            We map workflows, fix operational inefficiencies, and implement Videra to give teams a single
            source of truth, automated governance, and audit-ready reporting. Four ways to engage —
            from a short advisory check-in to a full programme transformation.
          </p>
          <Link to="/contact" className="inline-flex items-center mt-6 text-sm font-medium text-slate-900 hover:text-blue-600 transition-colors group">
            Talk to our team <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
        <div className="lg:col-span-6">
          <div className="grid grid-cols-2 gap-3">
            {[
              ['Advisory',  'Day rate or retainer', 'Outside-in view, board prep, audits.'],
              ['Workshop',  '1–3 days on-site',     'Mapping sessions, gate redesign, evidence walk-throughs.'],
              ['Embedded',  'Weeks or months',      'On-the-ground PMs working inside your team.'],
              ['Programme', 'Full transformation',  'End-to-end redesign + Videra deployment.'],
            ].map(([title, sub, desc]) => (
              <div key={title} className="bg-white border border-slate-200 rounded-xl p-5 hover:border-slate-400 hover:shadow-md transition-all">
                <p className="text-base font-medium text-slate-900">{title}</p>
                <p className="text-xs text-blue-600 font-medium mt-0.5">{sub}</p>
                <p className="mt-3 text-xs text-slate-500 leading-snug">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ─────────── 10. FINAL CTA ─────────── */}
    <section className="py-24 md:py-32 bg-slate-950 text-white text-center relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.05]" style={{
        backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
        backgroundSize: '32px 32px',
      }} />
      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs uppercase tracking-[0.2em] text-blue-400 font-semibold mb-4">Let&apos;s talk</p>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight">
          This is delivery with certainty.
        </h2>
        <p className="mt-5 text-base md:text-lg text-slate-400 max-w-2xl mx-auto">
          Whether you want us in the room or you&apos;d rather try a sector workspace yourself —
          we&apos;d love to help you get your delivery onto one rail.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/contact" className="inline-flex items-center justify-center bg-white text-slate-900 px-8 py-4 font-medium tracking-wide hover:bg-slate-100 transition-all rounded-md group">
            Talk to Pete <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
          <a href="https://demo.videra.app" className="inline-flex items-center justify-center bg-white/10 hover:bg-white/15 border border-white/20 text-white px-8 py-4 font-medium tracking-wide transition-all rounded-md group">
            Try the Videra demo <CheckCircle2 className="ml-2 w-4 h-4 text-emerald-400" />
          </a>
        </div>
      </div>
    </section>
  </div>
);

export default V2About;
